import type { ReactElement } from 'react';
import { useEffect, useMemo } from 'react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import type { ExtensionListItem } from '@kirin/api-client/contracts/extensions';
import { queryKeys } from '@/hooks/api/queryKeys';
import { trackEvent } from '@/lib/analytics';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { createExtensionColumns } from './columns';
import { fetchUserExtensions } from './api';
import { EXTENSION_TABLE_CONFIG } from './constants';
import { AgentmeshRiskDetails } from '@/components/shared/extensions';

function getQueryKeysFromParams(params: DataFetchParams) {
  return queryKeys.extensions.personalDataTable({
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search: params.search,
  });
}

/**
 * DataTable-based extensions tab for the user dashboard.
 * Shows personal extensions with filtering, sorting, and search.
 */
export function DataTablePersonalExtensionsContent(): ReactElement {
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.CAPABILITY_DETECTION,
    flagsmithFeatureFlags.AGENTMESH_RISK_ESTIMATE,
  ]);
  const capabilityDetectionEnabled =
    flags[flagsmithFeatureFlags.CAPABILITY_DETECTION] ?? false;
  const agentmeshRiskEnabled =
    flags[flagsmithFeatureFlags.AGENTMESH_RISK_ESTIMATE] ?? false;
  const columns = useMemo(
    () =>
      createExtensionColumns(capabilityDetectionEnabled, agentmeshRiskEnabled),
    [capabilityDetectionEnabled, agentmeshRiskEnabled]
  );

  // Track tab view for analytics (mirrors legacy ExtensionsTab behavior)
  useEffect(() => {
    trackEvent('extensions_tab_viewed', { context: 'personal' });
  }, []);

  return (
    <DataTable<ExtensionListItem>
      id={EXTENSION_TABLE_CONFIG.tableId}
      mode="server"
      className="flex flex-col flex-1 min-h-0"
      containerClassName="flex-1"
      columns={columns}
      errorBoundaryLabel="Something went wrong in the extensions table"
      search={{
        placeholder: 'Search extensions...',
        className: 'w-[260px]',
      }}
      features={{
        enableSorting: true,
        enableRowExpansion: agentmeshRiskEnabled,
      }}
      rowExpansion={
        agentmeshRiskEnabled
          ? {
              render: (extension: ExtensionListItem) => (
                <AgentmeshRiskDetails riskEstimate={extension.riskEstimate} />
              ),
              getRowId: row => row.extensionId,
              expandOnRowClick: true,
              showContainer: false,
              contentClassName: '',
            }
          : undefined
      }
      queryFn={(params, signal) => fetchUserExtensions(params, signal)}
      getQueryKeysFromParams={getQueryKeysFromParams}
      noDataLabel="No extensions installed. Install extensions in your IDE to see them here."
      queryConfig={{ staleTime: 30000, refetchOnWindowFocus: false }}
    />
  );
}

DataTablePersonalExtensionsContent.displayName =
  'DataTablePersonalExtensionsContent';

export default function DataTableExtensionsTab(): ReactElement {
  return <DataTablePersonalExtensionsContent />;
}
